// file: src/pages/home/HomePage.jsx
import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, Zap, Terminal, Brain, Shield, Cloud, MessageSquare, BarChart3, Users, Settings, Lock, ClipboardList, DraftingCompass, Factory } from 'lucide-react';
import styles from './HomePage.module.css';
import TaskBar from '../../components/taskbar/TaskBar';
import Footer from '../../components/footer/Footer';
import ImageSlideshow from '../../components/slideshow/ImageSlideshow';

// Open source tool icons
import blenderIcon from '../../components/assets/blender_file.svg';
import freecadIcon from '../../components/assets/freecad_file.svg';
import kicadIcon from '../../components/assets/kicad_file.svg';
import jupyterIcon from '../../components/assets/jupyter_notebook.svg';

/**
 * HomePage component - Public marketing page for unauthenticated users
 * Based on Factory.ai aesthetic with light/dark mode compliance
 */
export default function HomePage() {
  return (
    <>
      <TaskBar />
      <div className={styles.homePage}>
        {/* Hero Section */}
        <section className={styles.hero}>
        <div className={styles.heroContent}>
          <h1 className={styles.heroTitle}>
            An AI Engineer on<br />
            Every Hardware Project
          </h1>
          <p className={styles.heroSubtitle}>
          Start your 7 day free trial ($25 credit) today
          </p>
          <div className={styles.heroActions}>
            <Link to="/login" className={styles.primaryButton}>
              Start building
            </Link>
          </div>
        </div>
      </section>

      {/* Capabilities Section - Image Slideshow */}
      <section className={styles.capabilities}>
        <ImageSlideshow />
      </section>

      {/* Open Source Tools Section */}
      <section className={styles.ossTools}>
        <h2 className={styles.sectionTitle}>
          Build with your favorite<br />
          open source tools
        </h2>
        <p className={styles.sectionSubtitle}>
          Foundry provides cloud workspaces for industry-leading open source CAD and development tools
        </p>
        <div className={styles.ossToolsScroller}>
          <div className={styles.ossToolsTrack}>
            {/* Multiple sets for seamless infinite scrolling */}
            {[...Array(4)].map((_, setIndex) => (
              <React.Fragment key={setIndex}>
                <div className={styles.ossToolItem}>
                  <img src={blenderIcon} alt="Blender" className={styles.ossToolIcon} />
                  <span className={styles.ossToolName}>Blender</span>
                </div>
                <div className={styles.ossToolItem}>
                  <img src={freecadIcon} alt="FreeCAD" className={styles.ossToolIcon} />
                  <span className={styles.ossToolName}>FreeCAD</span>
                </div>
                <div className={styles.ossToolItem}>
                  <img src={kicadIcon} alt="KiCad" className={styles.ossToolIcon} />
                  <span className={styles.ossToolName}>KiCad</span>
                </div>
                <div className={styles.ossToolItem}>
                  <img src={jupyterIcon} alt="JupyterLab" className={styles.ossToolIcon} />
                  <span className={styles.ossToolName}>JupyterLab</span>
                </div>
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      {/* Main Value Proposition */}
      <section className={styles.valueProp}>
        <h2 className={styles.sectionTitle}>
          AI that will supercharge you,<br />
          not replace you
        </h2>
        <p className={styles.sectionSubtitle}>Your intelligent co-pilot that amplifies your expertise and accelerates your workflow</p>
        
        <div className={styles.featuresGrid}>
          <div className={styles.featureCard}>
            <div className={styles.featureIcon}>
              <MessageSquare />
            </div>
            <h3>Collaborate with AI</h3>
            <p>Work alongside AI agents that understand your projects, answer questions in real-time, and provide guidance every step of the way.</p>
          </div>
          
          <div className={styles.featureCard}>
            <div className={styles.featureIcon}>
              <Brain />
            </div>
            <h3>Instant Expert Insights</h3>
            <p>Get intelligent design suggestions, catch potential issues early, and make data-driven decisions with AI-powered analysis.</p>
          </div>
          
          <div className={styles.featureCard}>
            <div className={styles.featureIcon}>
              <Zap />
            </div>
            <h3>Automate the Tedious</h3>
            <p>Let AI handle repetitive tasks, documentation, and routine checks so you can focus on creative problem-solving and innovation.</p>
          </div>
        </div>
      </section>

      {/* Advanced Features */}
      <section className={styles.advancedFeatures}>
        <h2 className={styles.sectionTitle}>
          The most advanced Cloud Platform<br />
          for Designing Hardware
        </h2>
        <p className={styles.sectionSubtitle}>
          Design, build, test and ship on one platform, with full traceability and integration with your mission-critical infrastructure.
        </p>
        
        <div className={styles.featuresGrid}>
          <div className={styles.featureCard}>
            <div className={styles.featureIcon}>
              <Users />
            </div>
            <h3>Collaborate on Projects</h3>
            <p>Foundry is designed to be shared. You can spin up as many projects and share with as many people as you like.</p>
          </div>
          
          <div className={styles.featureCard}>
            <div className={styles.featureIcon}>
              <Terminal />
            </div>
            <h3>Cloud Servers</h3>
            <p>Run your workstreams in purpose-built cloud development environments shared with your team.</p>
          </div>
          
          <div className={styles.featureCard}>
            <div className={styles.featureIcon}>
              <Lock />
            </div>
            <h3>Governance</h3>
            <p>Open-core architecture with zero lock-in. Maintain complete control over your data, IP, and usage policies across cloud or on-premises deployments.</p>
          </div>
          
          <div className={styles.featureCard}>
            <div className={styles.featureIcon}>
              <Shield />
            </div>
            <h3>Security</h3>
            <p>Enterprise-grade security with end-to-end encryption and comprehensive audit trails. Your IP stays protected at every layer.</p>
          </div>
          
          <div className={styles.featureCard}>
            <div className={styles.featureIcon}>
              <Settings />
            </div>
            <h3>Integrations</h3>
            <p>Power up your projects by connecting custom tools and services with full MCP support.</p>
          </div>
          
          <div className={styles.featureCard}>
            <div className={styles.featureIcon}>
              <Cloud />
            </div>
            <h3>Our Cloud or Yours</h3>
            <p>Run your workstreams in our secure cloud or yours.</p>
          </div>
        </div>
      </section>

      {/* TODO(@jessy): Add enterprise section back in after SOC2 audit is complete. */}
      {/* Enterprise Section
      <section className={styles.enterprise}>
        <h2 className={styles.sectionTitle}>Enterprise-grade Tooling for everyone</h2>
        
        <div className={styles.enterpriseFeatures}>
          <div className={styles.enterpriseFeature}>
            <h3>Self-Hosted</h3>
            <p>Deploy Factory within your own infrastructure for complete control over data and compute resources while maintaining all platform capabilities. Customize configurations to meet your regulatory requirements and integrate seamlessly with existing tools.</p>
          </div>
          
          <div className={styles.enterpriseFeature}>
            <h3>Enterprise Grade Security</h3>
            <p>Factory employs state-of-the-art security protocols to protect your intellectual property while maintaining compliance with SOC II, GDPR, ISO 42001, and CCPA standards.</p>
          </div>
          
          <div className={styles.enterpriseFeature}>
            <h3>SSO + SAML</h3>
            <p>Seamlessly integrate with your existing identity management systems through Single Sign-On and SAML support with every major provider.</p>
          </div>
          
          <div className={styles.enterpriseFeature}>
            <h3>Safe Autonomy Readiness Policy</h3>
            <p>Our Safe Autonomy Readiness Policy establishes a comprehensive framework for identifying, evaluating, mitigating, and monitoring AI risks to ensure secure and transparent enterprise deployment.</p>
          </div>
        </div>
      </section> */}

      {/* CTA Section */}
      <section className={styles.cta}>
        <div className={styles.ctaContent}>
          <h2 className={styles.ctaTitle}>Ready to build the future?</h2>
          <p className={styles.ctaSubtitle}>
            Start your 7 day free trial ($25 credit) today
          </p>
          <Link to="/login" className={styles.primaryButton}>
            Start building
          </Link>
        </div>
      </section>

        <Footer />
      </div>
    </>
  );
}
